import moment, { Moment, MomentInput } from "moment";
import {
  DateFormat,
  TimeFormat,
  useTimeFormatStore,
} from "../store/time-format";

/**Returns ISO date format (YYYY-MM-DDTHH:mm:ss) but without the timezone (Z).  */
export function getCurrentDateString() {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set time to midnight without affecting timezone

  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0"); // Ensure two digits
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}T00:00:00`;
}

// ---------------------------------------------------------------------------
// US 922643: 12/24-hour time format and the user's date format.
// Every time shown to the user goes through these helpers so the user's
// preference (store/time-format) is applied in one place. Do not hardcode
// "hh:mm A" / toLocaleTimeString() in screens. Values sent to the API
// (payloads, ISO strings, datetime-local values) are machine formats and must
// NOT use these helpers.
// ---------------------------------------------------------------------------

export const TIME_FORMAT_12H: TimeFormat = "12h";
export const TIME_FORMAT_24H: TimeFormat = "24h";
export const DEFAULT_TIME_FORMAT: TimeFormat = TIME_FORMAT_12H;

/** Only the exact values EAM web stores are accepted ("12h" / "24h"). */
export const isValidTimeFormat = (value: unknown): value is TimeFormat =>
  value === TIME_FORMAT_12H || value === TIME_FORMAT_24H;

export const DEFAULT_DATE_FORMAT: DateFormat = "MM/DD/YYYY";

// EAM web's date format values use the same tokens as moment, so each value is
// also its moment pattern.
const DATE_FORMATS: DateFormat[] = [
  "MM/DD/YYYY",
  "MM-DD-YYYY",
  "DD/MM/YYYY",
  "DD-MM-YYYY",
  "YYYY-MM-DD",
];

/** Only the exact values EAM web's Edit Preferences saves are accepted. */
export const isValidDateFormat = (value: unknown): value is DateFormat =>
  DATE_FORMATS.includes(value as DateFormat);

const TIME_PATTERNS: Record<TimeFormat, { minutes: string; seconds: string }> =
  {
    "12h": { minutes: "hh:mm A", seconds: "hh:mm:ss A" },
    "24h": { minutes: "HH:mm", seconds: "HH:mm:ss" },
  };

// Time-only strings from the server, e.g. eq_readings_history.reading_time.
const TIME_ONLY_REGEX = /^\d{1,2}:\d{2}(:\d{2}(\.\d+)?)?$/;
const TIME_ONLY_PATTERNS = ["HH:mm:ss.SSS", "HH:mm:ss", "HH:mm"];

export type TimeFormatOptions = {
  /** Include seconds. Default false. */
  seconds?: boolean;
  /** The value is UTC without an offset and must be shown in local time. */
  fromUtc?: boolean;
};

export type DateTimeFormatOptions = TimeFormatOptions & {
  /** moment pattern for the date part. Default: device locale date. */
  datePattern?: string;
};

/** Current preference, readable outside React (models, helpers). */
export const getTimeFormat = (): TimeFormat => {
  const timeFormat = useTimeFormatStore.getState().timeFormat;
  return isValidTimeFormat(timeFormat) ? timeFormat : DEFAULT_TIME_FORMAT;
};

/** The user's date format as a moment pattern, e.g. "DD/MM/YYYY". */
export const getDatePattern = (): string => {
  const dateFormat = useTimeFormatStore.getState().dateFormat;
  return isValidDateFormat(dateFormat) ? dateFormat : DEFAULT_DATE_FORMAT;
};

export const is24HourFormat = (timeFormat: TimeFormat = getTimeFormat()) =>
  timeFormat === TIME_FORMAT_24H;

/** moment pattern for the time part, e.g. "hh:mm A" or "HH:mm:ss". */
export const getTimePattern = (
  seconds = false,
  timeFormat: TimeFormat = getTimeFormat()
) => TIME_PATTERNS[timeFormat][seconds ? "seconds" : "minutes"];

const toMoment = (
  value: MomentInput,
  fromUtc = false
): Moment | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  if (typeof value === "string" && TIME_ONLY_REGEX.test(value.trim())) {
    return moment(value.trim(), TIME_ONLY_PATTERNS);
  }
  return fromUtc ? moment.utc(value).local() : moment(value);
};

/** Formats the time of day per the user's preference, e.g. "02:30 PM" / "14:30". */
export const formatTime = (
  value: MomentInput,
  { seconds = false, fromUtc = false }: TimeFormatOptions = {}
): string => {
  const m = toMoment(value, fromUtc);
  if (!m) return "";
  if (!m.isValid()) return String(value);
  return m.format(getTimePattern(seconds));
};

/** Formats date + time, with the time part per the user's preference. */
export const formatDateTime = (
  value: MomentInput,
  { seconds = false, fromUtc = false, datePattern }: DateTimeFormatOptions = {}
): string => {
  const m = toMoment(value, fromUtc);
  if (!m) return "";
  if (!m.isValid()) return String(value);
  const datePart = datePattern
    ? m.format(datePattern)
    : m.toDate().toLocaleDateString();
  return `${datePart} ${m.format(getTimePattern(seconds))}`;
};

/**
 * Timestamp written into comment headers (completion remarks, comments, long
 * description). Matches EAM web's header: the user's date format, the time
 * with seconds per the user's preference, then the short zone name, e.g.
 * "25/09/2026 14:30:05 IST". Pass the time the entry was written, so an
 * offline entry keeps that time rather than the time it syncs.
 */
export const formatCommentTimestamp = (date: Date = new Date()): string => {
  const stamp = formatDateTime(date, {
    seconds: true,
    datePattern: getDatePattern(),
  });
  const zone = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
    .formatToParts(date)
    .find((part) => part.type === "timeZoneName")?.value;
  return zone ? `${stamp} ${zone}` : stamp;
};

/** hourCycle for Ionic IonDatetime. */
export const getHourCycle = (
  timeFormat: TimeFormat = getTimeFormat()
): "h12" | "h23" => (is24HourFormat(timeFormat) ? "h23" : "h12");

/** FullCalendar slotLabelFormat / eventTimeFormat. */
export const getCalendarTimeFormat = (
  timeFormat: TimeFormat = getTimeFormat()
) =>
  is24HourFormat(timeFormat)
    ? ({ hour: "2-digit", minute: "2-digit", hour12: false } as const)
    : ({
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        meridiem: "short",
      } as const);
